import type {
  ContractFilterInput,
  ContractsGlobalSearchQuery,
  ContractsGlobalSearchQueryVariables,
} from "@/gql/graphql";
import { ContractStatusType, DealerUserRole } from "@/gql/graphql";
import { CONTRACTS_GLOBAL_SEARCH_QUERY } from "@/components/Navigation/GlobalHeader/GlobalSearch/global-search-queries";
import type {
  ContractAutocompleteOptions,
  GlobalSearchApolloClient,
} from "@/components/Navigation/GlobalHeader/GlobalSearch/types";
import {
  GLOBAL_SEARCH_PAGINATION_DEFAULTS,
  executeGlobalSearchQuery,
} from "./executeGlobalSearchQuery";

type ContractGlobalSearchNode = NonNullable<
  NonNullable<ContractsGlobalSearchQuery["contractsV2"]>["nodes"]
>[number];

function buildGlobalSearchContractsWhere(
  searchTerm: string,
  excludeSalesOrderAdminCreatedContracts: boolean
): ContractFilterInput | undefined {
  const trimmedSearchTerm = searchTerm.trim();
  if (!trimmedSearchTerm) {
    return undefined;
  }
  const andFilters: ContractFilterInput[] = [
    {
      or: [
        { contractNumberSearch: { contains: trimmedSearchTerm } },
        { customerName: { contains: trimmedSearchTerm } },
        { purchaseOrderNumber: { contains: trimmedSearchTerm } },
      ],
    },
  ];

  if (excludeSalesOrderAdminCreatedContracts) {
    andFilters.push({
      or: [
        { createdUserRole: { neq: DealerUserRole.SalesOrderAdmin } },
        { status: { neq: ContractStatusType.Draft } },
      ],
    });
  }

  return { and: andFilters };
}

export async function fetchContracts(
  client: GlobalSearchApolloClient,
  accountId: string,
  searchTerm: string,
  excludeSalesOrderAdminCreatedContracts: boolean,
  signal?: AbortSignal
): Promise<{ totalCount: number; items: ContractAutocompleteOptions[] }> {
  const where = buildGlobalSearchContractsWhere(
    searchTerm,
    excludeSalesOrderAdminCreatedContracts
  );
  if (!where) {
    return { totalCount: 0, items: [] };
  }
  const data = await executeGlobalSearchQuery<
    ContractsGlobalSearchQuery,
    ContractsGlobalSearchQueryVariables
  >({
    client,
    query: CONTRACTS_GLOBAL_SEARCH_QUERY,
    variables: { ...GLOBAL_SEARCH_PAGINATION_DEFAULTS, accountId, where },
    signal,
  });
  const response = data?.contractsV2;
  const contracts: ContractGlobalSearchNode[] = response?.nodes ?? [];
  return {
    totalCount: response?.totalCount ?? 0,
    items: contracts.map((c) => ({
      contractNumber: String(c.contractNumber),
      customerName: c.customerName,
      contractStatus: c.status,
      contractId: c.id,
      purchaseOrderNumber: c.purchaseOrderNumber ?? "",
    })),
  };
}
